/**
 * SmartBooker Dental · Smart Migration Engine™
 *
 * Premium upload + auto-mapping + preview + async import wizard.
 * Tenant must be dental; gated client-side by useIsDentalTenant + by the
 * backend require_dental_tenant + run_dental_migration permission.
 */
import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link, useNavigate } from "react-router-dom";
import { API, useAuth } from "../../App";
import { useIsDentalTenant } from "../../hooks/useIsDentalTenant";
import {
  Upload, FileSpreadsheet, FileText, Loader2, CheckCircle2, AlertTriangle,
  ArrowRight, ArrowLeft, Sparkles, Database, ShieldCheck, Crown,
  Layers, RefreshCw, X, Info,
} from "lucide-react";
import { toast } from "sonner";

const PROVIDERS = [
  { key: "auto", label: "Auto-rilevamento", desc: "Rileva il formato dal file" },
  { key: "csv", label: "CSV generico", desc: "File .csv esportato dal tuo gestionale" },
  { key: "xlsx", label: "Excel (.xlsx)", desc: "Foglio Excel" },
  { key: "telericetta", label: "TeleRicetta", desc: "Export ufficiale TeleRicetta" },
  { key: "alfadocs", label: "AlfaDocs", desc: "Export ufficiale AlfaDocs" },
];

const CANONICAL_FIELDS = [
  { key: "first_name", label: "Nome" },
  { key: "last_name", label: "Cognome" },
  { key: "email", label: "Email" },
  { key: "phone", label: "Telefono" },
  { key: "birth_date", label: "Data di nascita" },
  { key: "address", label: "Indirizzo" },
  { key: "city", label: "Città" },
  { key: "fiscal_code", label: "Codice fiscale" },
  { key: "gender", label: "Sesso" },
  { key: "notes", label: "Note" },
  { key: "tags", label: "Tag" },
  { key: "anamnesis", label: "Anamnesi" },
  { key: "appointment_date", label: "Data appuntamento" },
  { key: "appointment_time", label: "Ora appuntamento" },
  { key: "appointment_service", label: "Servizio" },
  { key: "appointment_staff", label: "Operatore" },
  { key: "treatment_name", label: "Trattamento" },
  { key: "treatment_date", label: "Data trattamento" },
  { key: "treatment_notes", label: "Note trattamento" },
  { key: "treatment_amount", label: "Importo" },
  { key: "document_path", label: "Documento" },
];

const MigrationPage = () => {
  const isDental = useIsDentalTenant();
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(1); // 1 upload · 2 mapping · 3 running · 4 done
  const [provider, setProvider] = useState("auto");
  const [file, setFile] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [job, setJob] = useState(null);
  const [mapping, setMapping] = useState({});
  const [confirming, setConfirming] = useState(false);
  const [poll, setPoll] = useState(null);
  const [logs, setLogs] = useState([]);
  const [pastJobs, setPastJobs] = useState([]);
  const [dragOver, setDragOver] = useState(false);

  // Gate: redirect non-dental tenants
  useEffect(() => {
    if (!authLoading && user && !isDental) {
      toast.error("Funzionalità riservata agli studi odontoiatrici");
      navigate("/dashboard");
    }
  }, [authLoading, user, isDental, navigate]);

  // Past jobs list
  const loadPastJobs = async () => {
    try {
      const r = await axios.get(`${API}/dental/migration/jobs`, { withCredentials: true });
      setPastJobs(r.data?.jobs || []);
    } catch { /* ignore */ }
  };
  useEffect(() => { if (isDental) loadPastJobs(); }, [isDental]);

  /* ---------- Upload ---------- */
  const handleFile = (f) => {
    if (!f) return;
    const okExt = /\.(csv|xlsx|xls|txt)$/i.test(f.name);
    if (!okExt) {
      toast.error("Formato non supportato. Usa CSV o XLSX.");
      return;
    }
    if (f.size > 25 * 1024 * 1024) {
      toast.error("File troppo grande (max 25 MB)");
      return;
    }
    setFile(f);
  };

  const onDrop = (e) => {
    e.preventDefault(); setDragOver(false);
    handleFile(e.dataTransfer.files?.[0]);
  };

  const startAnalysis = async () => {
    if (!file) return;
    setAnalyzing(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("provider", provider);
      const r = await axios.post(`${API}/dental/migration/upload`, fd, {
        withCredentials: true,
        headers: { "Content-Type": "multipart/form-data" },
      });
      setJob(r.data);
      setMapping(r.data.suggested_mapping || {});
      setStep(2);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore caricamento file");
    } finally {
      setAnalyzing(false);
    }
  };

  /* ---------- Mapping confirm + import ---------- */
  const confirmAndImport = async () => {
    if (!job) return;
    setConfirming(true);
    try {
      await axios.post(`${API}/dental/migration/${job.job_id}/confirm`, { mapping }, {
        withCredentials: true,
      });
      setStep(3);
      pollStatus(job.job_id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore avvio import");
    } finally {
      setConfirming(false);
    }
  };

  /* ---------- Polling ---------- */
  const pollStatus = (jobId) => {
    if (poll) clearInterval(poll);
    const tick = async () => {
      try {
        const r = await axios.get(`${API}/dental/migration/${jobId}/status`, { withCredentials: true });
        setJob(prev => ({ ...prev, ...r.data }));
        const lr = await axios.get(`${API}/dental/migration/${jobId}/logs?limit=30`, { withCredentials: true });
        setLogs(lr.data?.logs || []);
        if (r.data.status === "completed" || r.data.status === "error") {
          clearInterval(p);
          setPoll(null);
          setStep(4);
          loadPastJobs();
        }
      } catch { /* ignore transient */ }
    };
    const p = setInterval(tick, 1500);
    setPoll(p);
    tick();
  };

  useEffect(() => () => { if (poll) clearInterval(poll); }, [poll]);

  /* ---------- Restart ---------- */
  const reset = () => {
    setStep(1); setFile(null); setJob(null); setMapping({}); setLogs([]);
    if (poll) clearInterval(poll);
    setPoll(null);
  };

  if (authLoading || !user || !isDental) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-slate-950">
        <Loader2 className="w-6 h-6 animate-spin text-violet-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950" data-testid="migration-page">
      {/* HEADER */}
      <div className="border-b border-slate-200/60 dark:border-white/5 bg-white/70 dark:bg-slate-950/80 backdrop-blur sticky top-0 z-30">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center gap-4">
          <Link to="/dashboard" className="p-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-white/5" data-testid="migration-back">
            <ArrowLeft className="w-4 h-4" />
          </Link>
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-gradient-to-r from-violet-600 to-cyan-600 text-white text-[9px] font-bold uppercase tracking-[0.18em]">
                <Crown className="w-2.5 h-2.5" /> Dental Suite
              </span>
              <span className="text-[10px] text-emerald-600 dark:text-emerald-400 font-bold uppercase tracking-wider">
                <Sparkles className="w-2.5 h-2.5 inline mr-0.5" /> Smart Migration Engine™
              </span>
            </div>
            <h1 className="text-2xl font-black tracking-tight mt-1">Importa i tuoi dati clinici.</h1>
            <p className="text-xs text-muted-foreground mt-0.5">
              Da TeleRicetta, AlfaDocs, CSV o Excel · auto-mapping intelligente · GDPR ready
            </p>
          </div>
          <Stepper step={step} />
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-8 space-y-6">
        {step === 1 && (
          <UploadStep
            file={file} setFile={handleFile}
            provider={provider} setProvider={setProvider}
            dragOver={dragOver} setDragOver={setDragOver}
            onDrop={onDrop}
            startAnalysis={startAnalysis}
            analyzing={analyzing}
            pastJobs={pastJobs}
          />
        )}
        {step === 2 && job && (
          <MappingStep
            job={job}
            mapping={mapping} setMapping={setMapping}
            onBack={() => setStep(1)}
            onConfirm={confirmAndImport}
            confirming={confirming}
          />
        )}
        {(step === 3 || step === 4) && job && (
          <RunningStep
            job={job}
            logs={logs}
            done={step === 4}
            onReset={reset}
          />
        )}
      </div>
    </div>
  );
};

/* ---------------- Stepper ---------------- */
const Stepper = ({ step }) => {
  const steps = ["Upload", "Mapping", "Import"];
  return (
    <div className="hidden sm:flex items-center gap-1.5" data-testid="migration-stepper">
      {steps.map((label, i) => {
        const idx = i + 1;
        const active = step >= idx;
        const current = step === idx || (step === 4 && idx === 3);
        return (
          <div key={label} className="flex items-center gap-1.5">
            <div className={`w-7 h-7 rounded-full flex items-center justify-center text-[11px] font-bold transition-colors ${
              current ? "bg-gradient-to-br from-violet-600 to-cyan-600 text-white shadow"
                : active ? "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300"
                : "bg-slate-100 dark:bg-white/5 text-slate-400"
            }`}>
              {step > idx || (step === 4) ? <CheckCircle2 className="w-3.5 h-3.5" /> : idx}
            </div>
            <span className={`text-[11px] font-semibold ${current ? "text-slate-900 dark:text-white" : "text-slate-400"}`}>
              {label}
            </span>
            {i < steps.length - 1 && <span className="w-6 h-px bg-slate-200 dark:bg-white/10 mx-1" />}
          </div>
        );
      })}
    </div>
  );
};

/* ---------------- Step 1 — Upload ---------------- */
const UploadStep = ({ file, setFile, provider, setProvider, dragOver, setDragOver, onDrop, startAnalysis, analyzing, pastJobs }) => {
  const inputRef = React.useRef(null);
  return (
    <div className="grid lg:grid-cols-[1fr_320px] gap-6">
      <div className="space-y-4">
        {/* Drop zone */}
        <div
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={onDrop}
          onClick={() => inputRef.current?.click()}
          data-testid="migration-dropzone"
          className={`relative rounded-3xl border-2 border-dashed p-12 cursor-pointer transition-all ${
            dragOver
              ? "border-violet-400 bg-violet-50/50 dark:bg-violet-950/20"
              : "border-slate-200 dark:border-white/10 bg-white dark:bg-slate-900/40 hover:border-violet-300"
          }`}
        >
          <input
            ref={inputRef} type="file" accept=".csv,.xlsx,.xls,.txt"
            onChange={(e) => setFile(e.target.files?.[0])}
            className="hidden" data-testid="migration-file-input"
          />
          <div className="flex flex-col items-center text-center gap-3">
            <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-violet-500 to-cyan-500 flex items-center justify-center shadow-lg">
              <Upload className="w-6 h-6 text-white" />
            </div>
            {file ? (
              <>
                <p className="text-base font-bold text-slate-900 dark:text-white" data-testid="migration-file-name">
                  {file.name}
                </p>
                <p className="text-xs text-muted-foreground">
                  {(file.size / 1024).toFixed(1)} KB · clicca per cambiare
                </p>
              </>
            ) : (
              <>
                <p className="text-base font-bold text-slate-900 dark:text-white">
                  Trascina qui il file o clicca per scegliere
                </p>
                <p className="text-xs text-muted-foreground">
                  Supportiamo CSV, XLSX · max 25 MB
                </p>
              </>
            )}
          </div>
        </div>

        {/* Provider picker */}
        <div className="rounded-2xl border border-slate-200/60 dark:border-white/10 bg-white dark:bg-slate-900/40 p-4">
          <p className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-3">Sorgente dati</p>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            {PROVIDERS.map(p => (
              <button
                key={p.key}
                onClick={() => setProvider(p.key)}
                data-testid={`migration-provider-${p.key}`}
                className={`text-left rounded-xl border p-3 transition-colors ${
                  provider === p.key
                    ? "border-violet-500 bg-violet-50 dark:bg-violet-950/30"
                    : "border-slate-200 dark:border-white/10 hover:border-violet-300"
                }`}
              >
                <p className="text-sm font-bold">{p.label}</p>
                <p className="text-[10px] text-muted-foreground mt-0.5 leading-tight">{p.desc}</p>
              </button>
            ))}
          </div>
        </div>

        <button
          onClick={startAnalysis}
          disabled={!file || analyzing}
          data-testid="migration-analyze-btn"
          className="w-full rounded-full py-3.5 font-bold text-sm flex items-center justify-center gap-2 bg-gradient-to-r from-violet-600 to-cyan-600 hover:shadow-2xl text-white disabled:opacity-40 disabled:cursor-not-allowed transition-all"
        >
          {analyzing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
          {analyzing ? "Analisi in corso..." : "Analizza file e mostra preview"}
        </button>
      </div>

      {/* Sidebar */}
      <div className="space-y-4">
        <FeatureCard
          icon={Layers}
          title="Auto-mapping intelligente"
          desc="Riconosciamo automaticamente Nome, Cognome, Email, Telefono, Date e oltre 20 campi clinici."
        />
        <FeatureCard
          icon={ShieldCheck}
          title="Dedup automatico"
          desc="Email + telefono + nome usati come chiave: niente pazienti duplicati."
        />
        <FeatureCard
          icon={Database}
          title="Normalizzazione"
          desc="Date IT/EN, telefono in formato E.164, campi vuoti gestiti."
        />

        {pastJobs.length > 0 && (
          <div className="rounded-2xl border border-slate-200/60 dark:border-white/10 bg-white dark:bg-slate-900/40 p-4" data-testid="migration-past-jobs">
            <p className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-3">Import recenti</p>
            <ul className="space-y-1.5">
              {pastJobs.slice(0, 5).map(j => (
                <li key={j.job_id} className="flex items-center gap-2 text-[11px]">
                  <StatusDot status={j.status} />
                  <span className="font-semibold truncate flex-1">{j.file_name}</span>
                  <span className="text-muted-foreground">{j.imported || 0}/{j.stats?.total ?? "—"}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
};

const FeatureCard = ({ icon: Icon, title, desc }) => (
  <div className="rounded-2xl border border-slate-200/60 dark:border-white/10 bg-white dark:bg-slate-900/40 p-4">
    <div className="flex items-start gap-3">
      <span className="w-8 h-8 rounded-lg bg-violet-500/10 text-violet-600 dark:text-violet-300 flex items-center justify-center flex-shrink-0">
        <Icon className="w-4 h-4" />
      </span>
      <div>
        <p className="text-sm font-bold">{title}</p>
        <p className="text-[11px] text-muted-foreground mt-0.5 leading-snug">{desc}</p>
      </div>
    </div>
  </div>
);

const StatusDot = ({ status }) => {
  const map = {
    completed: "bg-emerald-500",
    running: "bg-cyan-500 animate-pulse",
    confirmed: "bg-amber-500",
    ready: "bg-slate-400",
    error: "bg-rose-500",
  };
  return <span className={`w-1.5 h-1.5 rounded-full ${map[status] || "bg-slate-400"}`} />;
};

/* ---------------- Step 2 — Mapping + Preview ---------------- */
const MappingStep = ({ job, mapping, setMapping, onBack, onConfirm, confirming }) => {
  const headers = job.headers || [];
  const preview = job.preview_rows || [];
  const stats = job.stats || {};
  const recognized = Object.values(mapping).filter(Boolean).length;
  const unrecognized = headers.length - recognized;

  return (
    <div className="space-y-6" data-testid="migration-mapping-step">
      <div className="rounded-3xl bg-gradient-to-br from-violet-600 to-cyan-600 p-6 text-white shadow-xl">
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <p className="text-[10px] font-bold uppercase tracking-[0.2em] opacity-80 mb-1">Step 2 di 3 · Mapping</p>
            <h2 className="text-2xl font-black">Verifica il riconoscimento dei campi</h2>
            <p className="text-sm opacity-90 mt-1">
              <FileText className="w-3.5 h-3.5 inline mr-1" /> {job.file_name} · {stats.total} righe · provider <strong>{job.provider}</strong>
            </p>
          </div>
          <div className="flex gap-3">
            <Stat value={recognized} label="Campi riconosciuti" tone="ok" />
            <Stat value={unrecognized} label="Da rivedere" tone={unrecognized > 0 ? "warn" : "ok"} />
          </div>
        </div>
      </div>

      <div className="grid lg:grid-cols-[1fr_1fr] gap-6">
        {/* Mapping table */}
        <div className="rounded-2xl border border-slate-200/60 dark:border-white/10 bg-white dark:bg-slate-900/40 overflow-hidden">
          <div className="px-4 py-3 border-b border-slate-200/60 dark:border-white/5 flex items-center justify-between">
            <div>
              <p className="text-sm font-bold">Mapping colonne</p>
              <p className="text-[10px] text-muted-foreground">Colonna originale → campo SmartBooker</p>
            </div>
          </div>
          <div className="max-h-[460px] overflow-y-auto p-3 space-y-1.5" data-testid="migration-mapping-table">
            {headers.map(h => {
              const v = mapping[h];
              return (
                <div key={h} className="grid grid-cols-[1fr_auto_1fr] items-center gap-2 p-2 rounded-lg hover:bg-slate-50 dark:hover:bg-white/5">
                  <span className="text-xs font-mono text-slate-700 dark:text-slate-200 truncate" title={h}>{h}</span>
                  <ArrowRight className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />
                  <select
                    value={v || ""}
                    onChange={(e) => setMapping({ ...mapping, [h]: e.target.value || null })}
                    data-testid={`migration-map-${h}`}
                    className={`text-xs rounded-md border px-2 py-1 bg-white dark:bg-slate-900 ${
                      v ? "border-violet-300 dark:border-violet-700" : "border-amber-300 dark:border-amber-700 bg-amber-50/30"
                    }`}
                  >
                    <option value="">— Ignora —</option>
                    {CANONICAL_FIELDS.map(c => (
                      <option key={c.key} value={c.key}>{c.label}</option>
                    ))}
                  </select>
                </div>
              );
            })}
          </div>
        </div>

        {/* Preview */}
        <div className="rounded-2xl border border-slate-200/60 dark:border-white/10 bg-white dark:bg-slate-900/40 overflow-hidden">
          <div className="px-4 py-3 border-b border-slate-200/60 dark:border-white/5">
            <p className="text-sm font-bold">Anteprima dati normalizzati</p>
            <p className="text-[10px] text-muted-foreground">Prime 5 righe · solo i campi mappati</p>
          </div>
          <div className="max-h-[460px] overflow-auto p-3" data-testid="migration-preview-table">
            {preview.slice(0, 5).map((row, i) => (
              <div
                key={`${row.fiscal_code || row.email || row.phone || ""}-${row.first_name || ""}-${row.last_name || ""}-${i}`}
                className="rounded-xl border border-slate-200/60 dark:border-white/10 p-3 mb-2 text-xs"
              >
                <p className="font-bold mb-1">
                  {row.first_name || "—"} {row.last_name || ""}
                </p>
                <div className="space-y-0.5 text-slate-600 dark:text-slate-300">
                  {row.email && <p>📧 {row.email}</p>}
                  {row.phone && <p>📞 {row.phone}</p>}
                  {row.birth_date && <p>🎂 {row.birth_date}</p>}
                  {row.city && <p>🏙 {row.city}</p>}
                  {row.anamnesis && <p>📋 {row.anamnesis}</p>}
                  {row.appointment_date && <p>📅 {row.appointment_date} {row.appointment_time || ""} · {row.appointment_service || ""}</p>}
                </div>
              </div>
            ))}
            {preview.length === 0 && (
              <p className="text-xs text-muted-foreground p-4 text-center">Nessuna riga in preview</p>
            )}
          </div>
        </div>
      </div>

      <div className="flex items-center justify-between gap-3">
        <button
          onClick={onBack}
          data-testid="migration-back-btn"
          className="rounded-full px-5 py-2.5 text-sm font-bold border border-slate-200 dark:border-white/10 hover:bg-slate-50 dark:hover:bg-white/5 inline-flex items-center gap-1.5"
        >
          <ArrowLeft className="w-3.5 h-3.5" /> Indietro
        </button>
        <button
          onClick={onConfirm}
          disabled={confirming}
          data-testid="migration-confirm-btn"
          className="rounded-full px-7 py-3 text-sm font-bold bg-gradient-to-r from-emerald-500 to-cyan-500 hover:shadow-2xl text-white inline-flex items-center gap-1.5 disabled:opacity-40"
        >
          {confirming ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
          Conferma e importa {stats.total} righe
        </button>
      </div>
    </div>
  );
};

const Stat = ({ value, label, tone }) => (
  <div className={`rounded-xl px-4 py-2 backdrop-blur ${tone === "warn" ? "bg-amber-200/30" : "bg-white/15"}`}>
    <p className="text-2xl font-black leading-none">{value}</p>
    <p className="text-[9px] uppercase tracking-wider opacity-90 mt-0.5">{label}</p>
  </div>
);

/* ---------------- Step 3/4 — Running / Done ---------------- */
const RunningStep = ({ job, logs, done, onReset }) => {
  const progress = job.progress || 0;
  const isError = job.status === "error";
  return (
    <div className="space-y-6" data-testid="migration-running-step">
      <div className={`rounded-3xl p-6 text-white shadow-xl ${
        isError ? "bg-gradient-to-br from-rose-600 to-rose-700"
        : done ? "bg-gradient-to-br from-emerald-600 to-cyan-600"
        : "bg-gradient-to-br from-violet-600 to-cyan-600"
      }`}>
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-2xl bg-white/15 backdrop-blur flex items-center justify-center flex-shrink-0">
            {isError ? <AlertTriangle className="w-6 h-6" />
              : done ? <CheckCircle2 className="w-6 h-6" />
              : <Loader2 className="w-6 h-6 animate-spin" />}
          </div>
          <div className="flex-1">
            <p className="text-[10px] font-bold uppercase tracking-[0.2em] opacity-80 mb-1">Step 3 di 3 · Import</p>
            <h2 className="text-2xl font-black">
              {isError ? "Si è verificato un errore"
                : done ? "Import completato"
                : "Import in corso..."}
            </h2>
            <p className="text-sm opacity-90 mt-1">
              {job.file_name} · {job.stats?.total || 0} righe totali
            </p>
          </div>
        </div>
        <div className="mt-5">
          <div className="h-2 bg-white/15 rounded-full overflow-hidden">
            <div
              className="h-full bg-white transition-all duration-500"
              style={{ width: `${progress}%` }}
              data-testid="migration-progress-bar"
            />
          </div>
          <p className="text-[11px] mt-2 opacity-90 font-mono" data-testid="migration-progress-text">{progress}% · {job.status}</p>
        </div>
        <div className="grid grid-cols-3 gap-3 mt-5">
          <Stat value={job.imported || 0} label="Importati" tone="ok" />
          <Stat value={job.skipped || 0} label="Duplicati saltati" tone="warn" />
          <Stat value={job.errors || 0} label="Errori" tone={job.errors ? "warn" : "ok"} />
        </div>
      </div>

      {/* Logs */}
      <div className="rounded-2xl border border-slate-200/60 dark:border-white/10 bg-white dark:bg-slate-900/40 overflow-hidden">
        <div className="px-4 py-3 border-b border-slate-200/60 dark:border-white/5 flex items-center justify-between">
          <p className="text-sm font-bold">Log di importazione</p>
          <span className="text-[10px] text-muted-foreground">{logs.length} eventi</span>
        </div>
        <div className="max-h-[400px] overflow-y-auto p-3 space-y-1 text-xs font-mono" data-testid="migration-logs">
          {logs.length === 0 && (
            <p className="text-muted-foreground p-3 text-center">Nessun log ancora</p>
          )}
          {logs.map((l, i) => (
            <div
              key={`${l.row_idx ?? "x"}-${i}-${(l.message || "").slice(0, 20)}`}
              className={`px-2 py-1 rounded ${
              l.level === "error" ? "text-rose-700 dark:text-rose-300 bg-rose-50/50 dark:bg-rose-950/30"
              : l.level === "warn" ? "text-amber-700 dark:text-amber-300 bg-amber-50/30 dark:bg-amber-950/20"
              : "text-slate-600 dark:text-slate-300"
            }`}>
              <span className="opacity-50 mr-1.5">{l.row_idx ? `#${l.row_idx}` : "·"}</span>
              {l.message}
            </div>
          ))}
        </div>
      </div>

      {done && (
        <div className="flex items-center justify-between gap-3">
          <Link
            to="/clients"
            className="rounded-full px-5 py-2.5 text-sm font-bold border border-slate-200 dark:border-white/10 hover:bg-slate-50 dark:hover:bg-white/5 inline-flex items-center gap-1.5"
            data-testid="migration-go-clients"
          >
            Vai alla lista clienti <ArrowRight className="w-3.5 h-3.5" />
          </Link>
          <button
            onClick={onReset}
            data-testid="migration-restart-btn"
            className="rounded-full px-5 py-2.5 text-sm font-bold bg-violet-600 hover:bg-violet-700 text-white inline-flex items-center gap-1.5"
          >
            <RefreshCw className="w-3.5 h-3.5" /> Importa un altro file
          </button>
        </div>
      )}
    </div>
  );
};

export default MigrationPage;
